import * as React from "react";
import { useState } from "react";
import { Box, Grid2 as Grid, Step, StepLabel, Stepper, Typography } from "@platform/style";

import {
  faBriefcase,
  faCheck,
  faSearch,
  faPeopleGroup,
  faGears,
} from "@fortawesome/sharp-regular-svg-icons";
import WorkTemplateGrid from "./WorkTemplateGrid";
import { TemplateV2 } from "../../../models/TemplateV2";
import { WorkGroup } from "../../../models/WorkGroup";
import WorkTemplateConfiguration from "./WorkTemplateConfiguration";
import WorkGroupConfiguration from "./WorkGroupConfiguration";
import WorkGroupCreate from "./WorkGroupCreate";
import VerifyConfiguration from "./VerifyConfiguration";
import WorkComplete from "./WorkComplete";
import { library } from "@fortawesome/fontawesome-svg-core";
import { useGetWithToken } from "@platform/core";
import {
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE,
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2,
  WORKITEM_BFF_API_BASE_USERS,
} from "../../../platform-workitems";
import { Resource } from "../../../models/Resource";
import { StyledStepIcon } from "./Stepper/StyledStepIcon";
import { StyledStepConnector } from "./Stepper/StyledStepConnector";
import { BusinessProcess } from "../../../models/BusinessProcess";
import ConfigurationProgress from "./ConfigurationProgress";

library.add(faSearch, faBriefcase, faCheck, faPeopleGroup, faGears);

const displaySteps = [
  "Select a Template",
  "Configure Work Template",
  "Configure Work Group",
  "Verify Template and Group",
];

export default function WorkTemplateBuilder() {
  const [activeStep, setActiveStep] = useState(0);
  const [totalSteps] = useState(displaySteps.length);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateV2>();
  const [integratedSystem, setIntegratedSystem] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<WorkGroup[]>();
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [snackBar, setSnackBar] = React.useState({
    SnackBarMessage: "",
    ShowSnackBarMessage: false,
  });
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const [dataTemplates, isLoadingTemplates, isErrorTemplates, refetchTemplates] = useGetWithToken<
    TemplateV2[]
  >(`${WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2}/templates`, []);

  const [dataGroups, isLoadingGroups, isErrorGroups, refetchGroups] = useGetWithToken<WorkGroup[]>(
    `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups`,
    []
  );

  const [
    dataBusinessProcesses,
    isLoadingBusinessProcesses,
    isErrorBusinessProcesses,
    refetchBusinessProcesses,
  ] = useGetWithToken<BusinessProcess[]>(
    `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2}/business-processes`,
    []
  );

  const [dataUsers, isLoadingUsers, isErrorUsers, refetchUsers] = useGetWithToken<Resource[]>(
    WORKITEM_BFF_API_BASE_USERS,
    []
  );

  const handleRowSelect = (row: TemplateV2) => {
    setSelectedTemplate(row);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleTemplateUpdate = (updatedTemplate: TemplateV2, integratedSystem: string) => {
    setSelectedTemplate(updatedTemplate);
    setIntegratedSystem(integratedSystem);
  };

  const handleIsCreateGroup = () => {
    setIsCreatingGroup(true);
  };

  const handleNext = () => {
    if (isCreatingGroup) {
      setIsCreatingGroup(false);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (isCreatingGroup) {
      setIsCreatingGroup(false);
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    }
  };

  const handleReset = () => {
    handleErrorMessage(null);
    refetchTemplates();
    refetchGroups();
    refetchUsers();
    setActiveStep(0);
  };

  function handleSnackBar(message: string, open: boolean) {
    const newSnackBar = { SnackBarMessage: message, ShowSnackBarMessage: open };
    setSnackBar(newSnackBar);
  }

  function handleErrorMessage(errorMessage: string | null) {
    setErrorMessage(errorMessage);
  }

  const getStepData = (step: number) => {
    if (activeStep == step) {
      switch (step) {
        case 0:
          return (
            <WorkTemplateGrid
              onRowSelect={(selectedTemplate: TemplateV2) => handleRowSelect(selectedTemplate)}
              isLoadingTemplates={isLoadingTemplates}
              isErrorTemplates={isErrorTemplates}
              isLoadingBusinessProcesses={isLoadingBusinessProcesses}
              isErrorBusinessProcesses={isErrorBusinessProcesses}
              refetchBusinessProcesses={refetchBusinessProcesses}
              refetchTemplates={refetchTemplates}
              templates={dataTemplates}
            />
          );
        case 1:
          return (
            <WorkTemplateConfiguration
              onHandleNext={handleNext}
              onHandleBack={handleBack}
              onTemplateUpdate={handleTemplateUpdate}
              selectedTemplate={selectedTemplate}
            />
          );
        case 2:
          return isCreatingGroup ? (
            <WorkGroupCreate
              onHandleNext={handleNext}
              onHandleBack={handleBack}
              onHandleSnackBar={handleSnackBar}
              onHandleErrorMessage={handleErrorMessage}
              isLoadingUsers={isLoadingUsers}
              isErrorUsers={isErrorUsers}
              refetchGroups={refetchGroups}
              refetchUsers={refetchUsers}
              onGroupUpdate={setSelectedGroups}
              resources={dataUsers}
              groups={dataGroups}
              selectedTemplate={selectedTemplate}
            />
          ) : (
            <WorkGroupConfiguration
              onHandleCreate={handleIsCreateGroup}
              onHandleNext={handleNext}
              onHandleBack={handleBack}
              isLoadingGroups={isLoadingGroups}
              isErrorGroups={isErrorGroups}
              refetchGroups={refetchGroups}
              onGroupUpdate={setSelectedGroups}
              groups={dataGroups}
              selectedTemplate={selectedTemplate}
            />
          );
        case 3:
          return (
            <VerifyConfiguration
              onHandleBack={handleBack}
              onHandleFinish={handleNext}
              onHandleSnackBar={handleSnackBar}
              onHandleErrorMessage={handleErrorMessage}
              refetchTemplates={refetchTemplates}
              refetchGroups={refetchGroups}
              snackBar={snackBar}
              errorMessage={errorMessage}
              selectedTemplate={selectedTemplate}
              onTemplateUpdate={handleTemplateUpdate}
              onGroupUpdate={setSelectedGroups}
              selectedGroups={selectedGroups}
              selectedIntegratedSystem={integratedSystem}
            />
          );
        default:
          return null;
      }
    }
  };

  function StepLabelCustom(props: any) {
    return (
      <div>
        <StepLabel slots={{ stepIcon: StyledStepIcon }}>
          <Typography>{props.label}</Typography>
        </StepLabel>
      </div>
    );
  }

  function StepCustom(props: { number: any; children: any; activeStep: any }) {
    let { number, children, activeStep } = props;
    return (
      <Box sx={{ height: "100%", display: number === activeStep ? "block" : "none" }}>
        {children}
      </Box>
    );
  }

  return (
    <Box
      height={"100%"}
      display={"grid"}
      gridTemplateRows={"min-content min-content min-content minmax(0, 1fr)"}
    >
      <Stepper
        sx={{ paddingBottom: "8px" }}
        alternativeLabel
        activeStep={activeStep}
        connector={<StyledStepConnector />}
        role={"tablist"}
      >
        {displaySteps.map((label, step) => {
          return (
            <Step key={label} role={"tab"}>
              <StepLabelCustom
                StepIconComponent={StyledStepIcon}
                label={label}
                number={step}
                activeStep={activeStep}
              />
            </Step>
          );
        })}
      </Stepper>

      <Box>
        <ConfigurationProgress
          activeStep={activeStep}
          totalSteps={totalSteps}
          errorMessage={errorMessage}
          onHandleErrorMessage={handleErrorMessage}
          selectedTemplate={selectedTemplate}
          selectedGroups={selectedGroups}
        />
      </Box>

      <Box>
        {activeStep === totalSteps && (
          <WorkComplete
            onHandleReset={handleReset}
            onHandleSnackBar={handleSnackBar}
            snackBar={snackBar}
          />
        )}
      </Box>

      <Box>
        {displaySteps.map((label, step) => (
          <StepCustom key={label} number={step} activeStep={activeStep}>
            {getStepData(step)}
          </StepCustom>
        ))}
      </Box>
    </Box>
  );
}
